import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { getDisplayName } from '../lib/owner'

// userId → public identity (display_name / @username / avatar_url),
// sourced from my own user_profiles row + the friends_overview RPC.
// This replaces the pair-era KNOWN_NAMES map on every friend-facing
// surface; the hardcoded getDisplayName stays only as the last-resort
// fallback (it still knows the owner's bilingual name).
//
// Module-level cache so page hops don't flash UUID placeholders while
// the RPC round-trips; the next fetch refreshes it in place.

let cachedEntries = null

export function useUserDirectory() {
  const { user } = useAuth()
  const [entries, setEntries] = useState(cachedEntries || [])

  useEffect(() => {
    if (!user) return undefined
    let alive = true
    Promise.all([
      supabase.rpc('friends_overview'),
      supabase
        .from('user_profiles')
        .select('user_id, username, display_name, avatar_url')
        .eq('user_id', user.id)
        .maybeSingle(),
    ]).then(([overview, mine]) => {
      if (!alive) return
      const list = []
      for (const r of overview.data || []) {
        list.push({
          user_id: r.other_user_id,
          username: r.username,
          display_name: r.display_name,
          avatar_url: r.avatar_url,
        })
      }
      if (mine.data) list.push(mine.data)
      cachedEntries = list
      setEntries(list)
    })
    return () => { alive = false }
  }, [user])

  return useMemo(() => {
    const map = new Map(entries.map((e) => [e.user_id, e]))
    // Returns { name, profile } — name is never null ('' when unknown),
    // profile feeds <Avatar profile={...}> for the avatar_url.
    const resolve = (userId, locale = 'en') => {
      const e = userId ? map.get(userId) : null
      const name = e?.display_name
        || (e?.username ? `@${e.username}` : '')
        || getDisplayName(userId, locale)
        || ''
      return { name, profile: e || null }
    }
    return { resolve, loaded: cachedEntries !== null }
  }, [entries])
}
